import { useState, useCallback } from 'react'
import './ContainerExecutionsSection.css'

interface WebhookEvent {
  id: string
  type: string
  container_name: string
  run_id: string | null
  timestamp: string
  data: string
  created_at: string
}

interface ContainerExecutionsSectionProps {
  containers: string[]
}

function formatTimestamp(ts: string): string {
  const date = new Date(ts)
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

function getEventTypeClass(type: string): string {
  if (type === 'container.error') return 'event-type-error'
  if (type.startsWith('container.')) return 'event-type-container'
  if (type.startsWith('message.')) return 'event-type-message'
  if (type.startsWith('task.')) return 'event-type-task'
  if (type === 'log') return 'event-type-log'
  return ''
}

function parseData(data: string): Record<string, unknown> {
  try {
    return typeof data === 'string' ? JSON.parse(data) : data as Record<string, unknown>
  } catch {
    return {}
  }
}

function formatEventData(type: string, data: Record<string, unknown>): string {
  switch (type) {
    case 'container.started':
      return [
        data.branch && `branch: ${String(data.branch)}`,
        data.revision && `rev: ${String(data.revision).slice(0, 8)}`,
      ].filter(Boolean).join(' \u00B7 ') || 'Started'
    case 'container.idle':
      return `${data.queueLength ?? 0} queued \u00B7 ${data.pendingTasks ?? 0} pending`
    case 'container.stopping':
      return 'Shutting down...'
    case 'container.stopped':
      return 'Stopped'
    case 'container.error':
      return data.error ? String(data.error).slice(0, 200) : 'Container creation failed'
    case 'task.started':
      return [
        data.skill && String(data.skill).split('/').pop(),
        data.subtasks && `${(data.subtasks as string[]).length} subtasks`,
      ].filter(Boolean).join(' \u00B7 ') || `attempt ${data.attempt ?? '?'}`
    case 'task.done': {
      const parts: string[] = []
      if (data.skill) parts.push(String(data.skill).split('/').pop()!)
      if (data.cost != null) parts.push(`$${Number(data.cost).toFixed(2)}`)
      if (data.totalCost != null) parts.push(`total: $${Number(data.totalCost).toFixed(2)}`)
      if (data.failed) parts.push('FAILED')
      return parts.join(' \u00B7 ') || 'Done'
    }
    default:
      return Object.keys(data).length > 0 ? JSON.stringify(data).slice(0, 120) : ''
  }
}

function ContainerExecutionsSection({ containers }: ContainerExecutionsSectionProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [events, setEvents] = useState<Record<string, WebhookEvent[]>>({})
  const [loading, setLoading] = useState<Record<string, boolean>>({})

  const toggleContainer = useCallback(async (containerName: string) => {
    const isExpanding = !expanded[containerName]
    setExpanded((prev) => ({ ...prev, [containerName]: isExpanding }))

    if (isExpanding && !events[containerName]) {
      setLoading((prev) => ({ ...prev, [containerName]: true }))
      try {
        const params = new URLSearchParams({ container_name: containerName, limit: '500' })
        const res = await fetch(`/api/webhook-events?${params.toString()}`)
        if (res.ok) {
          const data = await res.json() as WebhookEvent[]
          // Exclude log-type events
          const filtered = data.filter((e) => e.type !== 'log')
          setEvents((prev) => ({ ...prev, [containerName]: filtered }))
        }
      } catch {
        // best effort
      } finally {
        setLoading((prev) => ({ ...prev, [containerName]: false }))
      }
    }
  }, [expanded, events])

  if (containers.length === 0) return null

  return (
    <div className="container-executions-section">
      <h3 className="container-executions-heading">Container Executions</h3>
      {containers.map((containerName) => {
        const isExpanded = expanded[containerName] || false
        const containerEvents = events[containerName] || []
        const isLoading = loading[containerName] || false

        return (
          <div
            key={containerName}
            className="container-execution-item"
            data-testid={`container-${containerName}`}
          >
            <button
              className="container-execution-header"
              data-testid={`container-toggle-${containerName}`}
              onClick={() => toggleContainer(containerName)}
            >
              <span className={`container-toggle-icon ${isExpanded ? 'open' : ''}`}>
                {isExpanded ? '\u25BC' : '\u25B6'}
              </span>
              <span className="container-execution-name">{containerName}</span>
            </button>

            {isExpanded && (
              <div className="container-execution-details">
                {isLoading && (
                  <div className="container-execution-loading">Loading events...</div>
                )}

                {!isLoading && (
                  <>
                    <div className="container-execution-events-label">Recent Events:</div>
                    <table className="container-execution-events-table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Type</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {containerEvents.map((evt) => {
                          const data = parseData(evt.data)
                          return (
                            <tr key={evt.id}>
                              <td className="event-time">{formatTimestamp(evt.timestamp)}</td>
                              <td>
                                <span className={`container-event-type-badge ${getEventTypeClass(evt.type)}`}>
                                  {evt.type}
                                </span>
                              </td>
                              <td className="event-data">
                                {formatEventData(evt.type, data) || '-'}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default ContainerExecutionsSection
